import React, { useState, useRef, useEffect } from "react";
import makeApi, { URLS } from "Utils/apiURL";
import CallsUtils from "Utils/CallsUtils";
import { ReactComponent as SgLogo } from "Global/Header/Icons/salesgearLogo.svg";
import { ReactComponent as IPlay } from "./icons/iPlay.svg";
import { ReactComponent as IPause } from "./icons/iPause.svg";
import { ReactComponent as ICopy } from "./icons/iCopy.svg";
import { ReactComponent as ICheck } from "./icons/iCheck.svg";
import { ReactComponent as IUser } from "./icons/iUser.svg";
import { ReactComponent as ICal } from "./icons/iCal.svg";
import { ReactComponent as IClock } from "./icons/iClock.svg";
import { ReactComponent as ILink } from "./icons/iLink.svg";


const WAVEFORM_BARS = 80;
const waveHeights = Array.from({ length: WAVEFORM_BARS }, (_, i) => {
  const base = Math.sin(i * 0.4) * 0.3 + Math.sin(i * 0.13) * 0.4 + Math.sin(i * 0.07) * 0.3;
  return 14 + Math.abs(base) * 28;
});

function Waveform({ progress, onSeek }) {
  const filled = Math.floor((progress / 100) * WAVEFORM_BARS);

  const handleClick = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    onSeek(Math.max(0, Math.min(100, pct)));
  };

  return (
    <div className="shareRecordingWaveform" onClick={handleClick}>
      {waveHeights.map((h, i) => (
        <div
          key={i}
          className={`shareRecordingWaveBar ${i < filled ? "played" : "unplayed"}`}
          style={{ height: `${h}px` }}
        />
      ))}
    </div>
  );
}

function AudioPlayer({ src, totalSec }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [audioDur, setAudioDur] = useState(totalSec || 0);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onMeta = () => { setAudioDur(el.duration || totalSec || 0); setLoaded(true); };
    const onCanPlay = () => setLoaded(true);
    const onTimeUpdate = () => { if (el.duration) setProgress((el.currentTime / el.duration) * 100); };
    const onEnded = () => { setPlaying(false); setProgress(0); };
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("canplay", onCanPlay);
    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("canplay", onCanPlay);
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("ended", onEnded);
    };
  }, [src, totalSec]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    playing ? el.pause() : el.play();
    setPlaying(p => !p);
  };

  const seek = pct => {
    const el = audioRef.current;
    if (el && el.duration) el.currentTime = (pct / 100) * el.duration;
    setProgress(pct);
  };

  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  const currentSec = audioDur ? (progress / 100) * audioDur : 0;

  return (
    <>
      <audio ref={audioRef} src={src} preload="metadata" style={{ display: "none" }} />
      <Waveform progress={progress} onSeek={seek} />
      <div className="shareRecordingControls">
        <button className="shareRecordingPlayBtn" onClick={toggle} disabled={!loaded}>
          {playing ? <IPause style={{ color: "#fff" }} /> : <IPlay style={{ color: "#fff" }} />}
        </button>
        <div className="shareRecordingTime">
          {fmt(currentSec)}
          <span className="shareRecordingTimeSeparator"> / {CallsUtils.secondsToTime(audioDur)}</span>
        </div>
      </div>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ShareRecordingPage({ shareToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (window.Intercom) window.Intercom("update", { hide_default_launcher: true });
    return () => {
      if (window.Intercom) window.Intercom("update", { hide_default_launcher: false });
    };
  }, []);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const config = {
          url: `${URLS.getSharedRecordingDetails}${shareToken}`,
          method: "GET"
        };
        const res = await makeApi(config, undefined, false);
        if (res?.data?.status === "success") {
          setData(res.data);
        } else {
          setNotFound(true);
        }
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    if (shareToken) fetchDetails();
  }, [shareToken]);

  const handleCopy = url => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).catch(() => { });
    } else {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="shareRecordingPage">
        <div className="shareRecordingHeader">
          <div className="shareRecordingHeaderLeft" onClick={() => window.open("https://www.salesgear.io", "_blank")}>
            <SgLogo className="shareRecordingLogoHeader" />
            <span className="shareRecordingHeaderLabel">Call Recording</span>
          </div>
        </div>
        <div className="shareRecordingLoader">
          <div>
            <div className="shareRecordingSpinner" />
            <p className="shareRecordingLoadingText">Loading recording…</p>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="shareRecordingPage">
        <div className="shareRecordingHeader">
          <div className="shareRecordingHeaderLeft" onClick={() => window.open("https://www.salesgear.io", "_blank")}>
            <SgLogo className="shareRecordingLogoHeader" />
            <span className="shareRecordingHeaderLabel">Call Recording</span>
          </div>
        </div>
        <div className="shareRecordingNotFound">
          <div className="shareRecordingNotFoundCard">
            <div className="shareRecordingNotFoundEmoji">📭</div>
            <div className="shareRecordingNotFoundTitle">Recording not found</div>
            <div className="shareRecordingNotFoundSubtitle">This link may be invalid or no longer available.</div>
          </div>
        </div>
      </div>
    );
  }

  const {
    recordingUrl = "",
    contactName = "",
    companyName = "",
    callDate = "",
    duration = 0,
    outcome = "",
    callerName = "",
    toNumber = "",
    shareUrl = "",
  } = data;

  const displayName = contactName || toNumber || "Unknown Contact";

  const formatOutcome = (raw = "") => raw.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  const outcomeClass = outcome ? `shareRecordingOutcomeBadge${outcome.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()).replace(/\s+/g, "")}` : "";

  const formatDate = ts => {
    if (!ts) return "--";
    const d = new Date(ts);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const hour = d.getHours() % 12 || 12;
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const meridian = d.getHours() >= 12 ? "PM" : "AM";
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} at ${hour}:${minutes} ${meridian}`;
  };

  const metaItems = [
    callerName && { icon: <IUser style={{ color: "#9ca3af" }} />, label: `Called by ${callerName}` },
    callDate && { icon: <ICal style={{ color: "#9ca3af" }} />, label: formatDate(callDate) },
    duration > 0 && { icon: <IClock style={{ color: "#9ca3af" }} />, label: CallsUtils.secondsToTime(duration) },
  ].filter(Boolean);

  return (
    <div className="shareRecordingPage">
      <div className="shareRecordingHeader">
        <div className="shareRecordingHeaderLeft" onClick={() => window.open("https://www.salesgear.io", "_blank")}>
          <SgLogo className="shareRecordingLogoHeader" />
          <span className="shareRecordingHeaderLabel">Call Recording</span>
        </div>
      </div>

      <div className="shareRecordingContent">

        {/* Call meta card */}
        <div className="shareRecordingCard">
          <div className="shareRecordingCardHeader">
            <div className="shareRecordingContactRow">
              <div>
                <div className="shareRecordingContactName">{displayName}</div>
                {companyName && <div className="shareRecordingCompanyName">{companyName}</div>}
              </div>
              {outcome && <span className={`shareRecordingOutcomeBadge ${outcomeClass}`}>{formatOutcome(outcome)}</span>}
            </div>

            <div className="shareRecordingMetaRow">
              {metaItems.map((item, i) => (
                <div key={i} className="shareRecordingMetaItem">
                  {item.icon}
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {recordingUrl && (
            <div className="shareRecordingPlayerSection">
              <div className="shareRecordingPlayerLabel">Recording</div>
              <AudioPlayer src={recordingUrl} totalSec={duration} />
            </div>
          )}
        </div>

        {/* Share link card */}
        {shareUrl && (
          <div className="shareRecordingShareCard">
            <div className="shareRecordingShareLabel">Share this recording</div>
            <div className="shareRecordingShareRow">
              <div className="shareRecordingUrlBox">
                <ILink style={{ color: "#9ca3af" }} />
                <span className="shareRecordingUrlText">{shareUrl}</span>
              </div>
              <button
                className={`shareRecordingCopyBtn${copied ? " copied" : ""}`}
                onClick={() => handleCopy(shareUrl)}
              >
                {copied
                  ? <><ICheck style={{ color: "#fff" }} /> Copied</>
                  : <><ICopy style={{ color: "#fff" }} /> Copy link</>
                }
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="shareRecordingFooter" onClick={() => window.open("https://www.salesgear.io", "_blank")}>
        <SgLogo className="shareRecordingLogoFooter" />
        <span className="shareRecordingFooterPoweredBy">Powered by</span>
        <span className="shareRecordingFooterBrand">Salesgear</span>
      </div>
    </div>
  );
}
